import { gatewayGet, gatewayGetWithStatusCB, gatewayPost, gatewayPostWithStatusCB, webappGetWithOrigin, webappPostWithOrigin } from 'Fetch';

import { DPEnquiry } from 'Utilities/vehicles/@types';

import type { IncodeProgressStatus } from 'Containers/app/documentCapture/uploadDocuments/pages/IncodeSDKPage/IncodeSDKHelpers';

import { PostMarkAsCollectedBody, PostSaleStatusResponse } from '../containers/app/vehicleCollection/types/apis.types';
import { PremiumOffer } from '../utilities/profiling/@types';

import { oneTimeTokenResponse, valuatePayload, valuateResponse } from './api.types';
import { platformGet, platformPost } from './platformEndpoints';

// Platform top level endpoints (https://github.com/motorway/motorway-platform)
// eslint-disable-next-line camelcase
export const getMotorwayOffer = async (vrm: string, valuation_ref?: number | string) => platformGet('/premium-offer', { valuation_ref, vrm }, { ms: 10000 });
export const postValuate = async (payload: valuatePayload): valuateResponse => platformPost('/valuate', payload);
export const postOneTimeToken = async (abortController: AbortController): oneTimeTokenResponse => platformPost('/seller/generateOneTimeToken', { abortController });

// EDA Vehicle Checker endpoints
export const getMOTVehicleCheck = async (vrm: string, env: 'prod' | 'stage', updateStatusCB?: (status?: number) => void, abortController?: AbortController) => gatewayGetWithStatusCB(`/vehicle-checker/${env}/mot/checker/${vrm}`, undefined, updateStatusCB, { abortController });
export const postMotNotifications = async (payload: { dpSellerId?: number; vrm?: string}, env: 'prod' | 'stage', updateStatusCB?: (status?: number) => void, abortController?: AbortController) => gatewayPostWithStatusCB(`/vehicle-checker/${env}/mot/notifications`, payload, undefined, updateStatusCB, { abortController });

// Dealership Platform endpoints (https://github.com/motorway/motorway-dealership-platform)
export const getOfferBids = async (enquiryId: number, abortController?: AbortController) => gatewayGet('/premium/bids/offers', { enquiryId }, undefined, { abortController });
export const getValuationStatus = async (id: number, abortController?: AbortController) => gatewayGet(`/premium/vehicles/${id}`, { scope: 'state' }, undefined, { abortController });

// Dealership Platform v3 endpoints
export const acceptOffer = async (payload: { closingBidId: number; enquiryId: number }, abortController?: AbortController) => gatewayPost('/premium-v3/bids/accept-offer', payload, undefined, { abortController });
export const getEnquiryData = async (enquiryId: number, payload?: any, query = 'state,seller,vehicle,vehicleDetails'): Promise<DPEnquiry> => gatewayGet(`/premium-v3/enquiries/${enquiryId}`, { ...payload, ...(query.length && { include: query }) });
export const getSimpleEnquiryData = async (enquiryId: number, payload?: any, query = 'state,seller,vehicle,vehicleDetails') => gatewayGet(`/v3/enquiries/${enquiryId}`, { ...payload, ...(query.length && { include: query }) });
export const updateEnquiryData = async (enquiryId: number, payload: Partial<PremiumOffer>): Promise<DPEnquiry> => gatewayPost(`/premium-v3/enquiries/${enquiryId}/update`, { ...payload });

// Transaction Core endpoints (https://api-docs.motorway.co.uk/mw-transaction-core)
export const getShortUrl = async (enquiryId: number, payload?: {field: string; path: string}, abortController?: AbortController): Promise<Record<string, string>> => gatewayPost(`/transaction/${enquiryId}/generateShortUrl`, payload, undefined, { abortController });

// PostSale endpoints (https://github.com/motorway/mw-post-sale-experience-api)
export const getPostSaleStatus = async (enquiryId: string, header: { Authorization?: string }, updateStatusCB?: (status?: number) => void, abortController?: AbortController): Promise<PostSaleStatusResponse> => gatewayGetWithStatusCB(`/post-sale/enquiries/${enquiryId}/sale-status`, {}, updateStatusCB, { abortController }, {
  ...header,
  'Content-Type': 'application/json',
});
export const postMarkAsCollected = async (saleId: string, body: PostMarkAsCollectedBody, header: { Authorization?: string }, updateStatusCB?: (status?: number) => void, abortController?: AbortController): Promise<PostSaleStatusResponse> => gatewayPostWithStatusCB(`/post-sale/sales/${saleId}/collected`, body, {
  ...header,
  'Content-Type': 'application/json',
}, updateStatusCB, { abortController });
export const postIncodeVerificationUrl = async (enquiryId: number, header: { Authorization?: string }, payload?: any, abortController?: AbortController) => gatewayPost(`/post-sale/enquiries/${enquiryId}/verifications`, { ...payload }, {
  ...header,
  'Content-Type': 'application/json',
}, { abortController });
export const getIncodeVerificationStatus = async (enquiryId: number, header: { Authorization?: string }, payload?: any, abortController?: AbortController) => gatewayGet(`/post-sale/enquiries/${enquiryId}/verifications`, { ...payload }, undefined, { abortController }, {
  ...header,
  'Content-Type': 'application/json',
});
export const postIncodeVerificationProgress = async (
  saleId: string,
  header: { Authorization?: string },
  payload?: IncodeProgressStatus & { interviewId: string },
  abortController?: AbortController,
) => gatewayPost(`/documents-verification/v1/verifications/${saleId}/progress`, { ...payload }, {
  ...header,
  'Content-Type': 'application/json',
}, { abortController });

// Authn endpoints
export const authSignIn = async (payload: Record<string, string>) => webappPostWithOrigin('/api/authn/signin', payload);
export const authRefreshIdToken = async (
  payload: Record<string, string>,
  updateStatusCB?: (status?: number) => void,
) => webappPostWithOrigin('/api/authn/refreshtoken', payload, updateStatusCB, {
  priority: 'high',
});
export const authRefreshClientSession = async (payload: Record<string, string>) => webappPostWithOrigin('/api/authn/login', payload);
export const healthCheck = async () => webappGetWithOrigin('/api/health', undefined, { customIDHeaders: true });

// Payments endpoints
const PAYMENTS_APIS_TIMEOUT = 20000;
export const getBeneficiary = async (reference?: number | string | null, abortController?: AbortController, updateStatusCB?: () => void) => gatewayGetWithStatusCB(`/payments/beneficiaries/${reference}`, {}, updateStatusCB, { abortController }, {}, PAYMENTS_APIS_TIMEOUT, true);

// Eligibility service endpoints
export const getEligibilityCheck = (payload: string) => gatewayGet('/marketplace/check-eligibility/vehicle-status', { vrm: payload }, undefined, { useAccessNAuthToken: true });

// PaaS endpoints
export const paasEnquiry = async (enquiryId?: number | null) => webappPostWithOrigin('/api/paas/enquiry', { enquiryId });

// Seller Webapp endpoints
export const getUserLogOut = async () => webappGetWithOrigin('/api/logout');

// Vehicle Core endpoints (https://github.com/motorway/mw-vehicle-core)
export const submitDocuments = (payload: { enquiryId: number; kind?: string }, headers = {}) => gatewayPost('/vehicle/documents/review/markAsSubmitted', payload, headers);
